'use client';

import { useState, useEffect } from 'react';
import { uiFb } from '@/lib/i18n';
import { useScrollReveal } from './useScrollReveal';
import { getBestTimeSlugForCountry } from '@/lib/best-time-pages';
import { getCountrySlugFromCode } from '@/lib/long-weekends';
import { GEO_CACHE_KEY } from '@/hooks/useAutoCountry';
import type { CountryCode } from '@/lib/types';

interface LandingExploreStripProps {
  lang?: string;
}

type IconName = 'trip' | 'abroad' | 'religious' | 'leave';

/**
 * Inline SVG icons for the explore strip.
 * Themed via `currentColor` so they pick up the link's text color and the
 * dark/light theme tokens automatically. No HTTP fetch.
 *
 * Replaces 4 separate 48px PNG files (4 extra HTTP requests + no a11y).
 *
 * Refs: almanak-3pfa2.1.27
 */
function ExploreIcon({ name, className }: { name: IconName; className?: string }) {
  const common = {
    width: 48,
    height: 48,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className,
  };
  switch (name) {
    case 'trip': // paper plane
      return (
        <svg {...common}>
          <path d="M21 3 11 13" />
          <path d="m21 3-7 18-3-8-8-3 18-7Z" />
        </svg>
      );
    case 'abroad': // home with marker
      return (
        <svg {...common}>
          <path d="M3 11 12 4l9 7" />
          <path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
          <circle cx="12" cy="14" r="1.5" />
        </svg>
      );
    case 'religious': // crescent moon (universal religious-cycle symbol)
      return (
        <svg {...common}>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
          <path d="M17 5l1 1m1.5 1.5h2m-1.5 1.5l1 1" />
        </svg>
      );
    case 'leave': // palm / sunset
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 4v2" />
          <path d="M12 18v2" />
          <path d="M4.93 4.93 6.34 6.34" />
          <path d="M17.66 17.66l1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="M6.34 17.66l-1.41 1.41" />
          <path d="M19.07 4.93l-1.41 1.41" />
        </svg>
      );
  }
}

function getExploreCards(lang: string, countryCode?: CountryCode | null) {
  const bestTimeSlug = (countryCode && getBestTimeSlugForCountry(countryCode))
    || 'best-time-to-visit-indonesia';
  const lwSlug = (countryCode && getCountrySlugFromCode(countryCode))
    || 'india-long-weekends-2026';

  return [
    {
      iconName: 'trip' as IconName,
      title: uiFb('ui.explore.trip_title', 'Plan a trip', lang),
      body: uiFb('ui.explore.trip_body', 'Find the best time to visit any country', lang),
      href: `/${bestTimeSlug}`,
    },
    {
      iconName: 'abroad' as IconName,
      title: uiFb('ui.explore.abroad_title', 'Living abroad', lang),
      body: uiFb('ui.explore.abroad_body', 'Never miss a local holiday again', lang),
      href: '#countries',
    },
    {
      iconName: 'religious' as IconName,
      title: uiFb('ui.explore.religious_title', 'Religious calendars', lang),
      body: uiFb('ui.explore.religious_body', 'Islamic, Hindu, Buddhist & more', lang),
      href: '/islamic-calendar-2026-worldwide-calendar',
    },
    {
      iconName: 'leave' as IconName,
      title: uiFb('ui.explore.leave_title', 'Maximize time off', lang),
      body: uiFb('ui.explore.leave_body', 'Find long weekends and bridge days', lang),
      href: `/${lwSlug}`,
    },
  ];
}

export function LandingExploreStrip({ lang = 'en' }: LandingExploreStripProps) {
  const { ref, revealed } = useScrollReveal();
  const [countryCode, setCountryCode] = useState<CountryCode | null>(null);
  useEffect(() => {
    const cached = localStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      try {
        const { code } = JSON.parse(cached);
        if (code) setCountryCode(code as CountryCode);
      } catch { /* ignore malformed cache */ }
    }
  }, []);
  const cards = getExploreCards(lang, countryCode);

  return (
    <section ref={ref} className="px-4 py-6 md:px-8 md:py-10 lg:px-16 xl:px-20">
      <div className="mx-auto max-w-5xl">
        <p
          className={`scroll-reveal ${revealed ? 'revealed' : ''} mb-6 text-center text-sm font-semibold uppercase tracking-widest text-muted`}
        >
          {uiFb('ui.explore.heading', 'What can you do with Almanak?', lang)}
        </p>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {cards.map((card, i) => (
            <a
              key={card.title}
              href={card.href}
              className={`scroll-reveal ${revealed ? 'revealed' : ''} delay-${(i + 1) * 100} group flex flex-col items-center gap-2 rounded-xl border border-border-subtle bg-surface-card p-4 text-center text-text-primary transition-all hover:border-brand/40 hover:text-brand hover:shadow-md md:p-5`}
            >
              <ExploreIcon
                name={card.iconName}
                className="h-12 w-12 transition-transform group-hover:scale-110"
              />
              <span className="font-heading text-sm font-bold">{card.title}</span>
              <span className="text-label leading-snug text-text-secondary md:text-xs">{card.body}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
